import React, { useEffect, useState } from "react";
import { FiBell, FiZap, FiMenu, FiX, FiUser, FiPlusCircle, FiBookmark, FiLogOut, FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import * as api from "../api/index";
import { GiWallet } from "react-icons/gi";

const Header = ({ setAuth, setUserId, setEmail, refreshAuth }) => {
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [balance, setBalance] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  const [name, setName] = useState(null);

  const token = localStorage.getItem("token");
  let userId = null;
  let typeofuser = null;

  const APP_STORE_URL = "https://apps.apple.com/us/app/ourasap/id6759667729";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.ourasap.app";

  if (token && typeof token === "string") {
    const decoded = jwtDecode(token);
    userId = decoded.id || decoded._id || "";
    typeofuser = decoded.typeofuser || "";
  }

  const handleLogout = () => {
    localStorage.removeItem("token");
    refreshAuth();
    setMenuOpen(false);
    setUserId("");
    setEmail("");
    navigate("/auth");
  };

  const fetchUnreadCount = async () => {
    if (!userId) return;
    try {
      const res = await api.getNotifications(userId);
      const notifications = res.data.notifications || [];
      const unread = notifications.filter((n) => !n.isRead).length;
      setUnreadNotifications(unread);
    } catch {
      setUnreadNotifications(0);
    }
  };

  const fetchUnreadMessages = async () => {
    if (!userId) return;
    try {
      const res = await api.countunreadMessages(userId);
      const unread = res.data.unreadCount || 0;
      setUnreadMessages(unread);
    } catch {
      setUnreadMessages(0);
    }
  };

  const fetchBalance = async () => {
    if (!userId) return;
    try {
      const balanceRes = await api.getUserBalance(userId);
      const fetchedBonus = Number(balanceRes.data.bonus) || 0;
      const fetchedAmount = Number(balanceRes.data.amount) || 0;
      setBalance(fetchedBonus + fetchedAmount);
      setProfileImage(
        balanceRes.data.profileImage ||
          "https://res.cloudinary.com/dvuq6vmiy/image/upload/v1758705055/istockphoto-1288129985-612x612_hhrco1.jpg"
      );
      setName(balanceRes.data.name || null);
    } catch {
      setBalance(0);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUnreadCount();
      fetchBalance();
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchBalance();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchUnreadMessages();
      const interval = setInterval(() => {
        fetchUnreadMessages();
      }, 60 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [userId]);

  useEffect(() => {
    refreshAuth();
  }, []);

  return (
      <div className="sticky top-0 z-[100] w-full">
         <div className="w-full bg-gray-900 text-white px-4 py-2 flex items-center justify-center gap-3 flex-wrap">
      <span className="text-xs sm:text-sm font-semibold">
        Download our App
      </span>

      <div className="flex items-center gap-2">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-white text-gray-900 hover:bg-orange-50 px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold transition-all active:scale-95"
        >
          <FaApple className="text-sm" /> App Store
        </a>

        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold transition-all active:scale-95"
        >
          <FaGooglePlay className="text-xs" /> Google Play
        </a>
      </div>
    </div>
    <header className="sticky top-0 z-[100] w-full bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm px-4 py-2.5 flex justify-between items-center">
      
      {/* LOGO (LEFT) */}
      <div
        onClick={() => {
          setMenuOpen(false);
          navigate("/");
        }}
        className="flex items-center gap-1.5 cursor-pointer group"
      >
        <div className="bg-orange-600 p-1.5 rounded-lg group-hover:rotate-12 transition-transform duration-300">
           <FiZap className="text-white text-lg" />
        </div>
        <span className="text-xl font-black tracking-tight text-gray-900">
          Our<span className="text-orange-600">ASAP</span>
        </span>
      </div>

      {/* RIGHT SIDE ACTIONS */}
      <div className="flex items-center gap-2 sm:gap-3">
        {userId ? (
          <>
            {/* Messages */}
            <button
              onClick={() => navigate("/conversations")}
              className="md:hidden relative p-2.5 rounded-full text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-all border border-transparent hover:border-orange-100"
            >
              <FiZap size={20} />
              {unreadMessages > 0 && (
                <span className="absolute top-2 right-2.5 w-2.5 h-2.5 bg-orange-600 border-2 border-white rounded-full" />
              )}
            </button>

            {/* Notifications */}
            <button
              onClick={() => navigate("/notification")}
              className="relative p-2.5 rounded-full text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-all border border-transparent hover:border-orange-100"
            >
              <FiBell size={20} />
              {unreadNotifications > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold h-4 w-4 flex items-center justify-center rounded-full border-2 border-white">
                  {unreadNotifications}
                </span>
              )}
            </button>

            {/* Profile Dropdown Toggle */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 pl-1 pr-3 py-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full transition-all"
              >
                <img
                  src={profileImage}
                  alt="profile"
                  className="w-8 h-8 rounded-full object-cover border border-white shadow-sm"
                />
                <span className="hidden md:block text-xs font-bold text-gray-700 max-w-[80px] truncate">
                  {name || "User"}
                </span>
                {menuOpen ? <FiX size={16} className="text-gray-400" /> : <FiMenu size={16} className="text-gray-400" />}
              </button>

              {/* DROPDOWN MENU */}
              {menuOpen && (
                <div className="absolute right-0 mt-3 w-52 bg-white shadow-2xl rounded-2xl border border-gray-100 p-2 z-[110] animate-in fade-in zoom-in duration-200">
                  <div className="px-3 py-2 border-b border-gray-50 mb-1">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Account</p>
                  </div>
                  
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      typeofuser === "Customer" || typeofuser === "Rider" ? navigate("/profile") : navigate("/profile-seller");
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors"
                  >
                    <FiUser className="text-lg" /> Profile
                  </button>

                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate(`/balancepage/${userId}`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors"
                  >
                    <GiWallet className="text-lg" /> Wallet
                  </button>

                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/sellproduct");
                    }}
                    className="md:hidden w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors"
                  >
                    <FiPlusCircle className="text-lg" /> Post Product
                  </button>

                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/bookmarkpage");
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors"
                  >
                    <FiBookmark className="text-lg" /> Bookmarks
                  </button>

                  <div className="my-1 border-t border-gray-50" />
                  
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <FiLogOut className="text-lg" /> Logout
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            {/* SELL BUTTON (RIGHT) */}
            {/* <button
              onClick={() => navigate("/sell-product")}
              className="flex items-center gap-1 bg-white border border-orange-200 text-orange-600 px-3 py-2 rounded-full font-bold text-xs active:scale-95 transition-all shadow-sm"
            >
              <FiPlus size={14} /> Sell
            </button> */}


            
            <button
              onClick={() => {
                setAuth(true);
                navigate("/auth");
              }}
              className="bg-orange-600 hover:bg-orange-700 active:scale-95 text-white text-xs sm:text-sm font-bold px-4 sm:px-6 py-2 sm:py-2.5 rounded-full shadow-lg shadow-orange-200 transition-all"
            >
              Get started
            </button>
          </div>
        )}
      </div>
    </header>
    </div>
  );
};

export default Header;